import { useState, useEffect, useCallback, useRef } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Upload, Trash2, Copy, Search, Image as ImageIcon, X } from "lucide-react";
import { toast } from "sonner";
import { api, mediaUrl } from "@/lib/api";
import { cn } from "@/lib/utils";

const MAX_FILES = 5000;
const PAGE_SIZE = 200;

export function MediaPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ done: 0, total: 0 });
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [dragOver, setDragOver] = useState(false);
  // Confirm-delete state: { mode: 'single' | 'bulk', id?: string }
  const [confirmDelete, setConfirmDelete] = useState(null);
  const fileInputRef = useRef(null);

  const fetchPage = useCallback(async (pageSkip, append) => {
    if (append) setLoadingMore(true); else setLoading(true);
    try {
      const { data } = await api.get("/media", {
        params: { skip: pageSkip, limit: PAGE_SIZE },
      });
      setItems((prev) => (append ? [...prev, ...(data.items || [])] : (data.items || [])));
      setTotal(data.total || 0);
      setSkip(pageSkip);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load media");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    fetchPage(0, false);
  }, [fetchPage]);

  const loadMore = () => fetchPage(items.length, true);

  const filtered = search
    ? items.filter((it) => it.filename.toLowerCase().includes(search.toLowerCase()))
    : items;

  const uploadFiles = async (files) => {
    if (!files || files.length === 0) return;
    if (total + files.length > MAX_FILES) {
      toast.error(`Library limit is ${MAX_FILES}. ${MAX_FILES - total} slots remaining.`);
      return;
    }

    setUploading(true);
    setUploadProgress({ done: 0, total: files.length });

    const CHUNK = 25;
    const chunks = [];
    for (let i = 0; i < files.length; i += CHUNK) {
      chunks.push(Array.from(files).slice(i, i + CHUNK));
    }

    let totalCreated = 0;
    let totalErrors = 0;
    for (const chunk of chunks) {
      const fd = new FormData();
      chunk.forEach((f) => fd.append("files", f));
      try {
        const { data } = await api.post("/media/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 180_000,
        });
        totalCreated += data.length;
      } catch (err) {
        totalErrors += chunk.length;
        toast.error(err?.response?.data?.detail || "Upload chunk failed");
      }
      setUploadProgress((p) => ({ ...p, done: p.done + chunk.length }));
    }

    setUploading(false);
    setUploadProgress({ done: 0, total: 0 });
    if (totalCreated > 0) toast.success(`Uploaded ${totalCreated} file${totalCreated === 1 ? "" : "s"}`);
    if (totalErrors > 0) toast.error(`${totalErrors} file${totalErrors === 1 ? "" : "s"} failed`);
    await fetchPage(0, false);
  };

  const onPickFiles = (e) => {
    uploadFiles(e.target.files);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer?.files) uploadFiles(e.dataTransfer.files);
  };

  const handleCopyUrl = async (item) => {
    const fullUrl = `${window.location.origin}${item.url}`;
    try {
      await navigator.clipboard.writeText(fullUrl);
      toast.success("URL copied to clipboard");
    } catch {
      toast.error("Could not copy. URL: " + fullUrl);
    }
  };

  const handleCopyAllUrls = async () => {
    const urls = filtered.map((it) => `${window.location.origin}${it.url}`).join("\n");
    try {
      await navigator.clipboard.writeText(urls);
      toast.success(`${filtered.length} URLs copied (one per line)`);
    } catch {
      toast.error("Clipboard write failed");
    }
  };

  const performDelete = async () => {
    if (!confirmDelete) return;
    try {
      if (confirmDelete.mode === "single") {
        await api.delete(`/media/${confirmDelete.id}`);
        setItems((prev) => prev.filter((it) => it.id !== confirmDelete.id));
        setTotal((t) => Math.max(0, t - 1));
        toast.success("Deleted");
      } else {
        const ids = Array.from(selected);
        await api.post("/media/bulk-delete", ids);
        setItems((prev) => prev.filter((it) => !selected.has(it.id)));
        setTotal((t) => Math.max(0, t - ids.length));
        setSelected(new Set());
        toast.success(`Deleted ${ids.length} file${ids.length === 1 ? "" : "s"}`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete");
    } finally {
      setConfirmDelete(null);
    }
  };

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map((it) => it.id)));
  };

  const hasMore = !search && items.length < total;

  return (
    <div className="space-y-6" data-testid="media-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-bold text-foreground">Media Library</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {total} of {MAX_FILES} files · drag & drop or click to upload PNG, JPG, SVG, WEBP, GIF
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/png,image/jpeg,image/svg+xml,image/webp,image/gif,.svg"
            className="hidden"
            onChange={onPickFiles}
            data-testid="media-upload-input"
          />
          <Button
            variant="premium"
            className="gap-2"
            disabled={uploading}
            onClick={() => fileInputRef.current?.click()}
            data-testid="media-upload-button"
          >
            <Upload className="w-4 h-4" />
            {uploading ? `Uploading ${uploadProgress.done}/${uploadProgress.total}...` : "Upload Files"}
          </Button>
          {filtered.length > 0 && (
            <Button variant="outline" className="gap-2" onClick={handleCopyAllUrls}>
              <Copy className="w-4 h-4" /> Copy All URLs
            </Button>
          )}
        </div>
      </div>

      {/* Drop zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => fileInputRef.current?.click()}
        className={cn(
          "border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all",
          dragOver ? "border-primary bg-primary/5" : "border-border hover:border-primary/50",
        )}
        data-testid="media-dropzone"
      >
        <Upload className="w-6 h-6 mx-auto mb-2 text-muted-foreground" />
        <p className="text-sm text-foreground font-medium">
          {dragOver ? "Drop to upload" : "Drag & drop logos here or click to browse"}
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          Up to {MAX_FILES} files. Max 10MB each. PNG, JPG, SVG, WEBP, GIF.
        </p>
      </div>

      {/* Search + bulk actions */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search filename..."
            className="pl-9"
          />
        </div>
        <div className="flex items-center gap-2">
          {filtered.length > 0 && (
            <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
              <Checkbox
                checked={selected.size === filtered.length && filtered.length > 0}
                onCheckedChange={toggleSelectAll}
                data-testid="media-select-all"
              />
              Select all ({selected.size}/{filtered.length})
            </label>
          )}
          {selected.size > 0 && (
            <Button
              variant="destructive"
              size="sm"
              className="gap-1"
              onClick={() => setConfirmDelete({ mode: "bulk" })}
              data-testid="media-bulk-delete"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Delete {selected.size}
            </Button>
          )}
        </div>
      </div>

      {loading ? (
        <Card className="glass-card">
          <CardContent className="p-12 text-center text-sm text-muted-foreground">
            Loading...
          </CardContent>
        </Card>
      ) : filtered.length === 0 ? (
        <Card className="glass-card">
          <CardContent className="p-12 text-center">
            <ImageIcon className="w-12 h-12 mx-auto mb-3 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              {items.length === 0 ? "No files yet. Drop some logos above to get started." : "No files match your search."}
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-3">
            {filtered.map((item) => {
              const isSelected = selected.has(item.id);
              return (
                <div
                  key={item.id}
                  className={cn(
                    "group relative rounded-lg border bg-card overflow-hidden transition-all",
                    isSelected ? "border-primary ring-2 ring-primary/40" : "border-border hover:border-primary/40"
                  )}
                  data-testid={`media-tile-${item.id}`}
                >
                  <div className="absolute top-1.5 left-1.5 z-10 opacity-0 group-hover:opacity-100 transition-opacity"
                    style={{ opacity: isSelected ? 1 : undefined }}
                  >
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => toggleSelect(item.id)}
                      className="bg-card"
                    />
                  </div>
                  <div className="aspect-square bg-secondary/30 flex items-center justify-center p-3">
                    <img
                      src={mediaUrl(item.url)}
                      alt={item.filename}
                      className="max-w-full max-h-full object-contain"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-2 space-y-1.5">
                    <p className="text-[11px] truncate font-medium" title={item.filename}>
                      {item.filename}
                    </p>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 text-[10px] flex-1 px-1.5"
                        onClick={() => handleCopyUrl(item)}
                      >
                        <Copy className="w-3 h-3 mr-1" /> URL
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 text-destructive hover:text-destructive"
                        onClick={() => setConfirmDelete({ mode: "single", id: item.id })}
                        data-testid={`media-delete-${item.id}`}
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {hasMore && (
            <div className="flex justify-center pt-2">
              <Button
                variant="outline"
                onClick={loadMore}
                disabled={loadingMore}
                data-testid="media-load-more"
              >
                {loadingMore ? "Loading..." : `Load more (${items.length}/${total})`}
              </Button>
            </div>
          )}
        </>
      )}

      {/* Confirm delete dialog */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(open) => { if (!open) setConfirmDelete(null); }}>
        <AlertDialogContent data-testid="media-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmDelete?.mode === "bulk"
                ? `Delete ${selected.size} file${selected.size === 1 ? "" : "s"}?`
                : "Delete this file?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              The file{confirmDelete?.mode === "bulk" ? "s" : ""} will no longer be reachable.
              Existing references in teams or leagues will break.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="media-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={performDelete}
              data-testid="media-confirm-delete"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
